import get from 'lodash/get';
/* istanbul ignore file */
import { useCallback, useContext, useEffect, useState } from 'react';
import { useDebounceCallback } from '@react-hook/debounce';
import usePagination from 'common/fetchHooks/usePagination';
import { RA_API_ERRORS } from 'common/services/resource-allocation.service';
import usePmSensorAuthId from 'components/hooks/PM/usePmSensorAuthId';
import useTableSortBy, { DEFAULT_SORT_BY } from './useTableSortBy';
import useRestoreResourceTypeIfNotExists from '../../useRestoreResourceTypeIfNotExists';
import { getResourceTypeDependencies } from '../../resource_types';
import useDefaultResourceType from '../../useDefaultResourceType';
import { ResourceAllocationFiltersContext } from '../../../state/filters/contexts';
import useResourceAllocationService from 'common/services/resource-allocation.service.versions';
import { useQuery } from '@tanstack/react-query';
import debounce from 'debounce-promise';
import useResourceTypes from '../../useResourceTypes';

const DEBOUNCE_TIMEOUT = 500;
export const DEFAULT_PAGINATION = {
  PAGE_SIZE: 25,
  PAGES: [10, 25, 50, 100]
};

const DEFAULT_QUERY_STRING_PARAM_NAME = 'defaultJiraStatsValue';
const useJiraStats = () => {
  const {
    state: {
      period,
      filtersPayload,
      extraPayload,
      isInitialized,
      filtersBarHeight
    }
  } = useContext(ResourceAllocationFiltersContext);
  const raService = useResourceAllocationService();
  const getAggregatedTableDebounced = useCallback(
    debounce(raService.getAggregatedTable, DEBOUNCE_TIMEOUT),
    []
  );

  const defaultResourceType = useDefaultResourceType(
    DEFAULT_QUERY_STRING_PARAM_NAME
  );
  const [resourceType, setResourceType] = useState(defaultResourceType);
  const pmSensorAuthId = usePmSensorAuthId();
  const [data, setData] = useState([]);
  const pagination = usePagination(DEFAULT_PAGINATION.PAGE_SIZE);
  const tableSortBy = useTableSortBy();
  const [keyword, setKeyword] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const { isLoading: isLoadingResourceTypes } = useResourceTypes();

  useRestoreResourceTypeIfNotExists({
    resourceType,
    restoreFn: () => setResourceType(defaultResourceType)
  });

  const handleFetchErrors = e => {
    const desc = get(e, 'response.data.description', '');
    if (desc.includes(RA_API_ERRORS.UNSUPPORTED_SORT_BY)) {
      tableSortBy.onChange(DEFAULT_SORT_BY);
    }
  };

  useQuery({
    enabled: isInitialized && !isLoadingResourceTypes,
    refetchOnWindowFocus: false,
    queryKey: [
      'allocationTable',
      period.before,
      period.after,
      ...getResourceTypeDependencies(resourceType),
      pagination.qs.limit,
      pagination.qs.offset,
      keyword,
      tableSortBy.key,
      tableSortBy.dir,
      filtersPayload
    ],
    queryFn: () => {
      setIsLoading(true);
      return getAggregatedTableDebounced({
        sensor_authorization_id: pmSensorAuthId,
        before: period.before,
        after: period.after,
        type: resourceType.type,
        issue_type: resourceType.issueType,
        limit: pagination.qs.limit,
        offset: pagination.qs.offset,
        keyword,
        sort_by: tableSortBy.key,
        sort_dir: tableSortBy.dir,
        ...(resourceType.extraParams && {
          extraParams: resourceType.extraParams
        }),
        filtersPayload,
        extraPayload
      });
    },
    onSuccess: res => {
      pagination.setTotal(get(res, 'total'));
      setData(get(res, 'data', []));
    },
    onError: e => {
      setData([]);
      handleFetchErrors(e);
    },
    onSettled: () => {
      setIsLoading(false);
    }
  });

  const resetPages = useDebounceCallback(
    () => pagination.setPage(1),
    DEBOUNCE_TIMEOUT
  );

  useEffect(() => {
    resetPages();
  }, [resourceType, period.before, period.after, keyword]);

  return {
    resourceType,
    setResourceType,
    keyword,
    setKeyword,
    pagination,
    data,
    isLoading,
    tableSortBy,
    filtersBarHeight
  };
};

export default useJiraStats;
